import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  FileText, 
  AlertTriangle, 
  Shield, 
  Trash2, 
  Eye,
  MessageSquare,
  Flag,
  Ban,
  CheckCircle
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { communityService } from '../services/communityService';

interface Report {
  id: string;
  type: 'post' | 'comment' | 'user';
  reason: string;
  description: string;
  status: 'PENDING' | 'REVIEWING' | 'RESOLVED' | 'DISMISSED';
  createdAt: string;
  reporter: {
    firstName: string;
    lastName: string;
  };
  post?: {
    id: string;
    title: string;
    content: string;
  };
  comment?: {
    id: string;
    content: string;
  };
  reportedUser?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface Stats {
  totalPosts: number;
  totalComments: number;
  totalReports: number;
  pendingReports: number;
  totalUsers: number;
}

export default function AdminCommunityDashboard() {
  const [reports, setReports] = useState<Report[]>([]);
  const [stats, setStats] = useState<Stats>({
    totalPosts: 0,
    totalComments: 0,
    totalReports: 0,
    pendingReports: 0,
    totalUsers: 0
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'resolved'>('pending');
  const [activeTab, setActiveTab] = useState<'reports' | 'posts'>('posts');
  const [allPosts, setAllPosts] = useState<any[]>([]);

  useEffect(() => {
    loadData();
  }, [filter, activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'reports') {
        // Load reports (API endpoint pentru admin)
        const reportsData = await communityService.getReports();
        const safeReports = Array.isArray(reportsData) ? reportsData : [];
        setReports(safeReports);

        // Calculate stats
        const pending = safeReports.filter((r: Report) => r.status === 'PENDING').length;
        setStats({
          totalPosts: safeReports.filter((r: Report) => r.type === 'post').length,
          totalComments: safeReports.filter((r: Report) => r.type === 'comment').length,
          totalReports: safeReports.length,
          pendingReports: pending,
          totalUsers: 0
        });
      } else {
        // Load ALL posts for admin control
        const postsData = await communityService.getPosts({ limit: 100 });
        setAllPosts(postsData.posts || []);
        setStats(prev => ({ ...prev, totalPosts: postsData.posts?.length || 0 }));
      }
    } catch (err: any) {
      console.error('Error loading admin data:', err);
      // Set empty data on error
      setReports([]);
      setAllPosts([]);
      setStats({
        totalPosts: 0,
        totalComments: 0,
        totalReports: 0,
        pendingReports: 0,
        totalUsers: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResolveReport = async (reportId: string, action: 'resolve' | 'dismiss') => {
    try {
      await communityService.resolveReport(reportId, action);
      loadData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Eroare la procesarea raportării');
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!confirm('Sigur vrei să ștergi acest post? Acțiunea este permanentă!')) return;
    
    try {
      await communityService.deletePost(postId);
      loadData();
      alert('Post șters cu succes!');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Eroare la ștergerea postului');
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm('Sigur vrei să ștergi acest comentariu?')) return;
    
    try {
      await communityService.deleteComment(commentId);
      loadData();
      alert('Comentariu șters cu succes!');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Eroare la ștergerea comentariului');
    }
  };

  const filteredReports = reports.filter(report => {
    if (filter === 'pending') return report.status === 'PENDING' || report.status === 'REVIEWING';
    if (filter === 'resolved') return report.status === 'RESOLVED' || report.status === 'DISMISSED';
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Se încarcă...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader title="Admin - Moderare Comunitate" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back to Admin */}
        <Link 
          to="/admin" 
          className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6"
        >
          ← Înapoi la Admin Dashboard
        </Link>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Posts</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalPosts}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Comentarii</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalComments}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Raportări</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalReports}</p>
              </div>
              <Flag className="w-8 h-8 text-orange-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-red-600">{stats.pendingReports}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Utilizatori</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
              </div>
              <Users className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Acțiuni Rapide
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/community"
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Eye className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium text-gray-900">Vezi Comunitatea</p>
                <p className="text-sm text-gray-600">Vizualizează ca utilizator</p>
              </div>
            </Link>

            <button
              onClick={loadData}
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
            >
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <div>
                <p className="font-medium text-gray-900">Refresh Raportări</p>
                <p className="text-sm text-gray-600">Reîncarcă datele</p>
              </div>
            </button>

            <div className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <Shield className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-medium text-gray-900">Status</p>
                <p className="text-sm text-green-600">Activ & Monitorizat</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs - Posts vs Reports */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="flex gap-4 border-b border-gray-200 pb-4 mb-4">
            <button
              onClick={() => setActiveTab('posts')}
              className={`px-6 py-3 font-semibold transition-colors ${
                activeTab === 'posts'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              📝 Toate Posts-urile ({allPosts.length})
            </button>
            <button
              onClick={() => setActiveTab('reports')}
              className={`px-6 py-3 font-semibold transition-colors ${
                activeTab === 'reports'
                  ? 'text-orange-600 border-b-2 border-orange-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              ⚠️ Raportări ({stats.totalReports})
            </button>
          </div>

          {/* Filters for Reports tab */}
          {activeTab === 'reports' && (
            <div className="flex gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Toate ({reports.length})
              </button>
              <button
                onClick={() => setFilter('pending')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === 'pending'
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Pending ({stats.pendingReports})
              </button>
              <button
                onClick={() => setFilter('resolved')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === 'resolved'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Rezolvate ({reports.length - stats.pendingReports})
              </button>
            </div>
          )}
        </div>

        {/* Content based on active tab */}
        {activeTab === 'posts' ? (
          /* ALL POSTS LIST */
          <div className="space-y-4">
            {allPosts.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Nu există posts în comunitate
                </h3>
                <p className="text-gray-600">
                  Utilizatorii nu au creat încă nicio postare.
                </p>
              </div>
            ) : (
              allPosts.map((post) => (
                <div key={post.id} className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                          {post.type}
                        </span>
                        {post.disease && (
                          <span className="text-sm text-gray-600">
                            📍 {post.disease.name}
                          </span>
                        )}
                      </div>
                      {post.title && (
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {post.title}
                        </h3>
                      )}
                      <p className="text-gray-700 mb-3 line-clamp-3">
                        {post.content}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>👤 {post.user.firstName} {post.user.lastName}</span>
                        <span>•</span>
                        <span>❤️ {post.likesCount} reactions</span>
                        <span>•</span>
                        <span>💬 {post._count?.comments || 0} comentarii</span>
                        <span>•</span>
                        <span>👁️ {post.viewsCount} vizualizări</span>
                        <span>•</span>
                        <span>{new Date(post.createdAt).toLocaleDateString('ro-RO')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Admin Actions */}
                  <div className="flex gap-2 pt-4 border-t border-gray-200">
                    <Link
                      to={`/community/posts/${post.id}`}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      Vezi Post
                    </Link>
                    <button
                      onClick={() => handleDeletePost(post.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      Șterge
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          /* REPORTS LIST */
          <div className="space-y-4">
            {filteredReports.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {filter === 'pending' ? 'Nu există raportări pending!' : 'Nu există raportări'}
              </h3>
              <p className="text-gray-600">
                {filter === 'pending' 
                  ? 'Toate raportările au fost procesate.' 
                  : 'Comunitatea este curată.'}
              </p>
            </div>
          ) : (
            filteredReports.map((report) => (
              <div key={report.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      report.type === 'post' ? 'bg-blue-100' :
                      report.type === 'comment' ? 'bg-green-100' :
                      'bg-purple-100'
                    }`}>
                      {report.type === 'post' && <FileText className="w-5 h-5 text-blue-600" />}
                      {report.type === 'comment' && <MessageSquare className="w-5 h-5 text-green-600" />}
                      {report.type === 'user' && <Users className="w-5 h-5 text-purple-600" />}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        Raportare {report.type === 'post' ? 'Post' : report.type === 'comment' ? 'Comentariu' : 'User'}
                      </p>
                      <p className="text-sm text-gray-600">
                        De la: {report.reporter.firstName} {report.reporter.lastName}
                      </p>
                    </div>
                  </div>

                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    report.status === 'PENDING' ? 'bg-orange-100 text-orange-800' :
                    report.status === 'REVIEWING' ? 'bg-yellow-100 text-yellow-800' :
                    report.status === 'RESOLVED' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {report.status}
                  </span>
                </div>

                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    Motiv: <span className="text-red-600">{report.reason}</span>
                  </p>
                  {report.description && (
                    <p className="text-sm text-gray-600">{report.description}</p>
                  )}
                </div>

                {/* Content Preview */}
                {report.post && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Post raportat:</p>
                    <p className="text-sm font-semibold text-gray-900">{report.post.title}</p>
                    <p className="text-sm text-gray-600 line-clamp-2">{report.post.content}</p>
                  </div>
                )}

                {report.comment && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Comentariu raportat:</p>
                    <p className="text-sm text-gray-600">{report.comment.content}</p>
                  </div>
                )}

                {report.reportedUser && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">User raportat:</p>
                    <p className="text-sm text-gray-900">
                      {report.reportedUser.firstName} {report.reportedUser.lastName}
                    </p>
                  </div>
                )}

                {/* Actions */}
                {(report.status === 'PENDING' || report.status === 'REVIEWING') && (
                  <div className="flex gap-2">
                    {report.post && (
                      <button
                        onClick={() => handleDeletePost(report.post!.id)}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Șterge Post
                      </button>
                    )}

                    {report.comment && (
                      <button
                        onClick={() => handleDeleteComment(report.comment!.id)}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Șterge Comentariu
                      </button>
                    )}

                    <button
                      onClick={() => handleResolveReport(report.id, 'resolve')}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Rezolvat
                    </button>

                    <button
                      onClick={() => handleResolveReport(report.id, 'dismiss')}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      <Ban className="w-4 h-4" />
                      Respinge
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
          </div>
        )}
      </div>
    </div>
  );
}
